import {
  type CreateReference,
  getReferenceData,
  getVersionReferenceData,
  isVersionReference,
  type ResourceType,
  type VersionReference,
} from '@quromedical/fhir-common';
import { flatten } from '@quromedical/utils';
import type { FhirResource, Reference } from 'fhir/r4';

export const filterResourcesByType = <
  TResult extends FhirResource,
  T extends FhirResource = FhirResource,
>(
  resourceType: TResult['resourceType'],
  resources: T[] = [],
): TResult[] =>
  resources.filter(
    (resource) => resource.resourceType === resourceType,
  ) as unknown as TResult[];

/**
 * Check if a resource matches the referenced `referenceResourceType` and `referenceId`
 * @param referenceResourceType type of resource
 * @param referenceId id of reference
 * @param resource resource to match
 */
export const doesResourceMatchReference = <T extends FhirResource>(
  referenceResourceType: ResourceType,
  referenceId: string,
  resource: T,
): boolean =>
  resource.resourceType === referenceResourceType &&
  resource.id === referenceId;

/**
 * Find resources that are referenced in the `references` array
 * @param references references to search for
 * @param resources resources to find references from
 * @returns list of `resources` that are referenced in `references
 */
export const findReferencedResources = <T extends FhirResource>(
  references: Reference[],
  resources: T[],
) => {
  const foundResources = references.map((ref) => {
    const referenceData = getReferenceData(ref);

    if (!referenceData) {
      return [];
    }

    const { resourceType, id } = referenceData;

    return resources.filter((resource) =>
      doesResourceMatchReference(resourceType, id, resource),
    );
  });

  return flatten(foundResources);
};

/**
 * Find the resource that is referenced in the `reference` parameter
 */
export const findReferencedResource = <T extends FhirResource>(
  reference?: Reference,
  resources: T[] = [],
): T | undefined => {
  const referenceData = getReferenceData(reference);

  if (!referenceData) {
    return undefined;
  }

  return resources.find((resource) =>
    doesResourceMatchReference(
      referenceData.resourceType,
      referenceData.id,
      resource,
    ),
  );
};

export const isValidReference = (
  reference?: fhir4.Reference,
): reference is CreateReference => !!reference?.reference;

export const getValidReference = (
  reference?: fhir4.Reference,
): CreateReference | undefined => {
  if (isValidReference(reference)) {
    return reference;
  }

  return undefined;
};

export const getValidVersionReference = <T extends FhirResource>(
  resourceTypes: ResourceType<T>[],
  reference?: fhir4.Reference,
): VersionReference<T> | undefined => {
  const isReference = isVersionReference(reference);

  if (!isReference) {
    return undefined;
  }

  const { resourceType } = getVersionReferenceData(reference);

  const isValidResourceType = resourceTypes.includes(resourceType);

  if (!isValidResourceType) {
    return undefined;
  }

  return reference as VersionReference<T>;
};
